// =============================================================================
// Widget bodies for the "activity" domains: Fitness, Time Tracker and Diary.
// See ../shared.js for the props contract every body receives.
// =============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { Dumbbell, Clock, Timer } from 'lucide-react';
import { parseISO, startOfWeek, endOfWeek } from 'date-fns';
import { formatDate } from '../../../lib/dates';
import {
  StatTile,
  WidgetEmpty,
  WidgetList,
  WidgetRow,
  formatHours,
  isCompact
} from '../shared';

// --- small helpers -----------------------------------------------------------

const toDate = (value) => {
  if (!value) return null;
  try {
    const d = typeof value === 'string' ? parseISO(value) : new Date(value);
    return d && !Number.isNaN(d.getTime()) ? d : null;
  } catch {
    return null;
  }
};

const fmtDate = (date, lang, shape = 'short') => formatDate(date, shape, lang);

// Monday-based week around the parent-supplied `today`.
const weekBounds = (today) => {
  const base = today instanceof Date && !Number.isNaN(today.getTime()) ? today : new Date();
  return {
    start: startOfWeek(base, { weekStartsOn: 1 }),
    end: endOfWeek(base, { weekStartsOn: 1 }),
    now: base
  };
};

const inRange = (date, start, end) =>
  !!date && date.getTime() >= start.getTime() && date.getTime() <= end.getTime();

const oneLine = (value) =>
  String(value == null ? '' : value)
    .replace(/\s+/g, ' ')
    .trim();

// --- fitness -----------------------------------------------------------------

// Flattens data.fitness.workoutsBySport (with the legacy crossfitWorkouts
// migration Fitness.js applies) into a list, filtered by the scoped sport.
const collectWorkouts = (data, scope) => {
  const fitness = data?.fitness || {};
  const map =
    fitness.workoutsBySport ||
    (fitness.crossfitWorkouts ? { Crossfit: fitness.crossfitWorkouts } : {});
  const out = [];
  Object.keys(map || {}).forEach((sport) => {
    if (scope && scope !== 'all' && scope !== sport) return;
    const list = Array.isArray(map[sport]) ? map[sport] : [];
    list.forEach((w) => {
      if (!w) return;
      const day = toDate(w.date) || toDate(w.createdAt);
      out.push({
        sport,
        day,
        sortKey: day ? day.getTime() : 0,
        minutes: Number(w.duration) || 0,
        id: w.id
      });
    });
  });
  return out;
};

const FitnessWeek = ({ data, scope, size, today, t }) => {
  const workouts = useMemo(() => collectWorkouts(data, scope), [data, scope]);
  const { count, minutes } = useMemo(() => {
    const { start, end } = weekBounds(today);
    const week = workouts.filter((w) => inRange(w.day, start, end));
    return {
      count: week.length,
      minutes: week.reduce((acc, w) => acc + w.minutes, 0)
    };
  }, [workouts, today]);

  if (workouts.length === 0) {
    return <WidgetEmpty>{t('dashw.fitness.empty', 'Noch keine Workouts')}</WidgetEmpty>;
  }

  const sub = [];
  if (scope && scope !== 'all') sub.push(scope);
  if (minutes > 0) {
    sub.push(
      t('dashw.fitness.weekDuration', '{time} gesamt', { time: formatHours(minutes * 60) })
    );
  }

  return (
    <StatTile
      value={count}
      label={t('dashw.fitness.weekCount', 'Workouts diese Woche')}
      sub={sub.length > 0 ? sub.join(' · ') : null}
      tone={count > 0 ? 'accent' : 'muted'}
      icon={Dumbbell}
      compact={isCompact(size)}
    />
  );
};

const FitnessRecent = ({ data, scope, size, t, lang }) => {
  const recent = useMemo(() => {
    const list = collectWorkouts(data, scope);
    list.sort((a, b) => b.sortKey - a.sortKey);
    return list.slice(0, isCompact(size) ? 4 : 6);
  }, [data, scope, size]);

  if (recent.length === 0) {
    return <WidgetEmpty>{t('dashw.fitness.empty', 'Noch keine Workouts')}</WidgetEmpty>;
  }

  return (
    <WidgetList>
      {recent.map((w, i) => (
        <WidgetRow key={`${w.sport}-${w.id || i}`}>
          <span className="flex-1 min-w-0 truncate text-sm text-gray-900 dark:text-gray-100">
            {w.sport}
          </span>
          {w.minutes > 0 && (
            <span className="flex-shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">
              {formatHours(w.minutes * 60)}
            </span>
          )}
          <span className="flex-shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
            {fmtDate(w.day, lang)}
          </span>
        </WidgetRow>
      ))}
    </WidgetList>
  );
};

// --- time tracker ------------------------------------------------------------

const trackerEntries = (data) => {
  const list = data?.timetracker?.entries;
  return Array.isArray(list) ? list.filter(Boolean) : [];
};

const findRunning = (entries) => entries.find((e) => e.startedAt && !e.endedAt) || null;

// Mirrors entrySeconds() in TimeTracker.js: an open entry counts up to `now`.
const entrySeconds = (entry, now) => {
  const start = toDate(entry?.startedAt);
  if (!start) return 0;
  const end = toDate(entry?.endedAt) || now || new Date();
  return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
};

const hhmmss = (seconds) => {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(
    2,
    '0'
  )}`;
};

const TimeTrackerWeek = ({ data, size, today, t }) => {
  const entries = useMemo(() => trackerEntries(data), [data]);
  const running = useMemo(() => findRunning(entries), [entries]);
  const { seconds, count } = useMemo(() => {
    const { start, end, now } = weekBounds(today);
    // TimeTracker.js buckets an entry by the week its start falls in.
    const week = entries.filter((e) => inRange(toDate(e.startedAt), start, end));
    return {
      seconds: week.reduce((acc, e) => acc + entrySeconds(e, now), 0),
      count: week.length
    };
  }, [entries, today]);

  if (entries.length === 0) {
    return (
      <WidgetEmpty>{t('dashw.timetracker.empty', 'Noch keine Zeiten erfasst')}</WidgetEmpty>
    );
  }

  let sub = null;
  if (running) {
    sub = t('dashw.timetracker.running', 'läuft');
  } else if (count === 1) {
    sub = t('dashw.timetracker.entriesOne', '1 Eintrag');
  } else if (count > 1) {
    sub = t('dashw.timetracker.entriesMany', '{n} Einträge', { n: count });
  }

  return (
    <StatTile
      value={formatHours(seconds)}
      label={t('dashw.timetracker.week', 'Diese Woche')}
      sub={sub}
      tone={running ? 'accent' : 'default'}
      icon={Clock}
      compact={isCompact(size)}
    />
  );
};

const TimeTrackerRunning = ({ data, size, t, lang }) => {
  const entries = useMemo(() => trackerEntries(data), [data]);
  const running = useMemo(() => findRunning(entries), [entries]);
  const start = running ? toDate(running.startedAt) : null;
  const startMs = start ? start.getTime() : null;

  // Ticks only while a timer runs; the interval is torn down as soon as the
  // running entry disappears (startMs -> null) and on unmount.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (startMs === null) return undefined;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startMs]);

  if (!running || startMs === null) {
    return <WidgetEmpty>{t('dashw.timetracker.noTimer', 'Kein Timer aktiv')}</WidgetEmpty>;
  }

  const companies = Array.isArray(data?.timetracker?.companies)
    ? data.timetracker.companies
    : [];
  const company = running.companyId
    ? (companies.find((c) => c && c.id === running.companyId) || {}).name || null
    : null;
  const title = oneLine(running.title);
  const label = title || company || t('dashw.timetracker.untitled', 'Ohne Titel');

  const sub = [];
  if (title && company) sub.push(company);
  if (!title && !company) sub.push(t('dashw.timetracker.noCompany', 'Ohne Firma'));
  sub.push(
    t('dashw.timetracker.since', 'seit {time}', { time: fmtDate(start, lang, 'time') })
  );

  return (
    <StatTile
      value={hhmmss(Math.floor((nowMs - startMs) / 1000))}
      label={label}
      sub={sub.join(' · ')}
      tone="accent"
      icon={Timer}
      compact={isCompact(size)}
    />
  );
};

// --- diary -------------------------------------------------------------------

const DiaryRecent = ({ data, size, t, lang }) => {
  const recent = useMemo(() => {
    const list = Array.isArray(data?.diary?.entries) ? data.diary.entries : [];
    return list
      .filter(Boolean)
      .map((e, i) => {
        const day = toDate(e.date) || toDate(e.createdAt);
        const text = oneLine(e.body) || oneLine(e.title);
        return {
          id: e.id || i,
          day,
          sortKey: day ? day.getTime() : 0,
          preview: text.length > 140 ? `${text.slice(0, 140)}…` : text
        };
      })
      .sort((a, b) => b.sortKey - a.sortKey)
      .slice(0, isCompact(size) ? 3 : 5);
  }, [data, size]);

  if (recent.length === 0) {
    return <WidgetEmpty>{t('dashw.diary.empty', 'Noch keine Einträge')}</WidgetEmpty>;
  }

  return (
    <WidgetList>
      {recent.map((entry) => (
        <WidgetRow key={entry.id}>
          <span className="flex-shrink-0 w-16 text-xs tabular-nums text-gray-500 dark:text-gray-400">
            {fmtDate(entry.day, lang)}
          </span>
          <span className="flex-1 min-w-0 truncate text-sm text-gray-900 dark:text-gray-100">
            {entry.preview || t('dashw.diary.noText', 'Kein Text')}
          </span>
        </WidgetRow>
      ))}
    </WidgetList>
  );
};

export const ACTIVITY_BODIES = {
  'fitness.week': FitnessWeek,
  'fitness.recent': FitnessRecent,
  'timetracker.week': TimeTrackerWeek,
  'timetracker.running': TimeTrackerRunning,
  'diary.recent': DiaryRecent
};
